import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { useFacilityStore } from '../../../app/z-store/store';
import { getItem } from '../../../utils/localStorage';

const LeaseAgreementReportTab = () => {
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const toast = useRef(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(10);
  
  // Current user - using consistent structure
  const [currentUser, setCurrentUser] = useState({
    userId: null,
    userName: null,
    fullName: null,
    email: null
  });
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('all');
  const [escalationFilter, setEscalationFilter] = useState('all');
  
  // Approval state
  const [approvalDialogVisible, setApprovalDialogVisible] = useState(false);
  const [currentApproval, setCurrentApproval] = useState(null);
  const [comments, setComments] = useState('');
  const [approving, setApproving] = useState(false);
  
  // Device info state
  const [deviceInfo, setDeviceInfo] = useState({
    deviceId: 'web-app',
    deviceType: 'web',
    ipAddress: 'unknown'
  });
  
  const rowsPerPageOptions = [10, 20, 50, 100];

  const statusOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Fully Paid', value: 'Fully Paid' },
    { label: 'Partially Paid', value: 'Partially Paid' },
    { label: 'Unpaid', value: 'Unpaid' }
  ];

  const escalationOptions = [
    { label: 'All Leases', value: 'all' },
    { label: 'With Escalations', value: 'with' },
    { label: 'Without Escalations', value: 'without' }
  ];

  // Get current user on mount - using consistent approach
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        console.log("Raw APPUSER from localStorage:", currentUserValue);
        
        let userData;
        
        // Parse the user data
        if (typeof currentUserValue === "string") {
          try {
            userData = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
            return;
          }
        } else {
          userData = currentUserValue;
        }
        
        // Extract user information with multiple fallbacks
        const user = userData?.user || userData;
        
        if (user) {
          // Get userId - check multiple possible fields
          const userId = user._id || user.userId || user.id;
          
          // Get userName - check multiple possible fields
          const getUserName = () => {
            if (user.fullName) return user.fullName;
            if (user.name) return user.name;
            if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
            if (user.username) return user.username;
            if (user.email) return user.email.split('@')[0];
            return 'Unknown User';
          };
          
          // Get email
          const userEmail = user.email || user.mail || null;
          
          setCurrentUser({
            userId,
            userName: getUserName(),
            fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            email: userEmail
          });
          
          console.log("Extracted user data:", {
            userId,
            userName: getUserName(),
            email: userEmail
          });
        } else {
          console.error("No user data found in APPUSER:", userData);
        }
      } catch (err) {
        console.error("Error getting current user:", err);
      }
    };

    getCurrentUser();
    
    // Get device information
    detectDeviceInfo();
    
    // Get IP address
    fetchIPAddress();
  }, []);

  // Detect device information
  const detectDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    let deviceType = 'web';
    let deviceId = 'web-app';

    // Detect device type
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      deviceType = 'mobile';
    } else if (/Tablet|iPad/i.test(userAgent)) {
      deviceType = 'tablet';
    }

    // Try to get a device identifier
    try {
      // Generate a consistent device ID
      const storedDeviceId = localStorage.getItem('lease_report_deviceId');
      if (storedDeviceId) {
        deviceId = storedDeviceId;
      } else {
        deviceId = 'web_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('lease_report_deviceId', deviceId);
      }
    } catch (error) {
      console.warn('Could not access localStorage for device ID:', error);
    }

    setDeviceInfo(prev => ({
      ...prev,
      deviceId,
      deviceType
    }));
  };

  // Fetch IP address
  const fetchIPAddress = async () => {
    try {
      // Try multiple IP detection methods
      const getIP = async () => {
        try {
          const response = await fetch('https://api.ipify.org?format=json');
          if (response.ok) {
            const data = await response.json();
            return data.ip;
          }
        } catch (error) {
          console.warn('Primary IP detection failed:', error);
        }
        
        // Fallback
        try {
          const response = await fetch('https://ipapi.co/json/');
          if (response.ok) {
            const data = await response.json();
            return data.ip;
          }
        } catch (error) {
          console.warn('Fallback IP detection failed:', error);
        }
        
        return 'unknown';
      };

      const ipAddress = await getIP();
      setDeviceInfo(prev => ({
        ...prev,
        ipAddress
      }));
    } catch (error) {
      console.error('Error fetching IP address:', error);
    }
  };

  useEffect(() => {
    if (selectedFacilityId) {
      fetchReportData();
      fetchCurrentApproval();
    }
  }, [selectedFacilityId, selectedMonth]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      // Format month as YYYY-MM
      const year = selectedMonth.getFullYear();
      const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
      const yearMonth = `${year}-${month}`;

      const response = await makeRequest2(
        `/api/app/lease_management/${selectedFacilityId}/lease-agreements/monthly-summary?month=${yearMonth}&limit=1000`,
        'GET'
      );

      if (response.success) {
        const leasesData = response.data.data?.leases || [];
        setReportData(leasesData);
        setStatistics(response.data.data?.statistics || null);
      } else {
        toastify('Failed to fetch lease agreement report', 'error');
        setReportData([]);
        setStatistics(null);
      }
    } catch (error) {
      toastify('Error fetching lease agreement report', 'error');
      setReportData([]);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentApproval = async () => {
    try {
      const response = await makeRequest2(
        `/api/app/levy_management/invoice-approvals/${selectedFacilityId}/lease/current`,
        'GET'
      );

      if (response.success && response.data.data) {
        setCurrentApproval(response.data.data);
      } else {
        setCurrentApproval(null);
      }
    } catch (error) {
      console.error('Error fetching current approval:', error);
      setCurrentApproval(null);
    }
  };

  const handleApproveInvoices = async () => {
    if (!currentApproval) {
      toastify('No approval entry found', 'error');
      return;
    }

    // Validate user data
    if (!currentUser.userId || !currentUser.userName) {
      console.error("Missing user data:", currentUser);
      toastify('User information not available. Please refresh the page and try again.', 'error');
      return;
    }

    try {
      setApproving(true);

      const payload = {
        action: 'approve',
        userId: currentUser.userId,
        userName: currentUser.userName,
        comments: comments.trim() || '',
        deviceInfo: {
          deviceId: deviceInfo.deviceId,
          deviceType: deviceInfo.deviceType,
          ipAddress: deviceInfo.ipAddress
        },
        priority: 8,
        delay: 0
      };

      console.log('Sending approval payload:', payload);

      const response = await makeRequest2(
        `/api/app/levy_management/invoice-approvals/${currentApproval._id}/action`,
        'POST',
        payload
      );

      if (response.success) {
        toastify('Invoice generation approved and triggered successfully', 'success');
        setApprovalDialogVisible(false);
        setComments('');
        
        // Refresh data
        await fetchReportData();
        await fetchCurrentApproval();
      } else {
        const errorMessage = response.data?.error || response.data?.message || 'Failed to approve invoice generation';
        toastify(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error approving invoice generation:', error);
      
      if (error.response?.data?.error) {
        toastify(error.response.data.error, 'error');
      } else if (error.message) {
        toastify(`Error: ${error.message}`, 'error');
      } else {
        toastify('Error approving invoice generation. Please try again.', 'error');
      }
    } finally {
      setApproving(false);
    }
  };

  const openApprovalDialog = () => {
    if (!currentApproval) {
      toastify('No pending approval found for this month', 'warning');
      return;
    }

    if (currentApproval.approvalStatus !== 'Pending') {
      toastify(`This approval has already been ${currentApproval.approvalStatus.toLowerCase()}`, 'warning');
      return;
    }

    // Check if user is available
    if (!currentUser.userId) {
      console.error("Current user ID is missing:", currentUser);
      toastify('Unable to verify user identity. Please refresh the page and try again.', 'error');
      return;
    }

    setApprovalDialogVisible(true);
  };

  // Format currency
  const formatCurrency = (value, currencyCode = 'KES') => {
    if (value === null || value === undefined) return `${currencyCode} 0.00`;
    return `${currencyCode} ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter data
  const filteredData = reportData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    
    const tenantName = item.tenant?.fullName?.toLowerCase() || '';
    const unitName = item.unit?.unitName?.toLowerCase() || '';
    const unitNumber = item.unit?.unitNumber?.toLowerCase() || '';
    
    const textMatch = tenantName.includes(searchLower) || 
                     unitName.includes(searchLower) || 
                     unitNumber.includes(searchLower);
    
    const statusMatch = statusFilter === 'all' || item.currentMonth?.paymentStatus === statusFilter;
    
    const escalationMatch = escalationFilter === 'all' || 
                           (escalationFilter === 'with' && item.hasEscalation) ||
                           (escalationFilter === 'without' && !item.hasEscalation);
    
    return textMatch && statusMatch && escalationMatch;
  });

  // Column templates
  const tenantBodyTemplate = (rowData) => {
    return (
      <div>
        <div className="fw-bold">{rowData.tenant?.fullName || 'N/A'}</div>
        <small className="text-muted">{rowData.tenant?.phoneNumber || ''}</small>
      </div>
    );
  };

  const unitBodyTemplate = (rowData) => {
    return (
      <div>
        <div className="fw-bold">{rowData.unit?.unitName || 'N/A'}</div>
        <small className="text-muted">
          {rowData.unit?.unitType || ''} {rowData.unit?.floor ? `- Floor ${rowData.unit.floor}` : ''}
        </small>
      </div>
    );
  };

  const currentInvoiceBodyTemplate = (rowData) => {
    const total = rowData.currentMonth?.totalInvoiced || 0;
    const currencyCode = rowData.currency?.code || 'KES';
    
    return (
      <div>
        <div className="fw-bold">{formatCurrency(total, currencyCode)}</div>
        <small className="text-muted">
          {rowData.currentMonth?.invoiceCount || 0} invoice(s)
        </small>
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    const status = rowData.currentMonth?.paymentStatus || 'Unknown';
    const statusColors = {
      'Fully Paid': 'bg-success text-light',
      'Partially Paid': 'bg-warning text-dark',
      'Unpaid': 'bg-danger text-light'
    };
    
    return (
      <span className={`badge ${statusColors[status] || 'bg-secondary text-light'}`}>
        {status}
      </span>
    );
  };

  const balanceBodyTemplate = (rowData) => {
    const balance = rowData.currentMonth?.totalBalance || 0;
    const bbf = rowData.balanceBroughtForward || 0;
    const currencyCode = rowData.currency?.code || 'KES';
    
    return (
      <div>
        <div className={balance > 0 ? 'text-danger fw-bold' : 'text-success'}>
          {formatCurrency(balance, currencyCode)}
        </div>
        {bbf !== 0 && (
          <small className="text-muted">BBF: {formatCurrency(bbf, currencyCode)}</small>
        )}
      </div>
    );
  };

  const nextMonthBodyTemplate = (rowData) => {
    const currencyCode = rowData.currency?.code || 'KES';
    const nextMonth = rowData.nextMonth;
    
    if (nextMonth?.expectedAmount !== undefined && nextMonth?.expectedAmount !== null) {
      const amount = nextMonth.expectedAmount;
      const hasUnpaidBalance = nextMonth.unpaidBalance > 0;
      const hasCredit = nextMonth.creditAmount > 0;
      const willBeAutoPaid = nextMonth.willBeAutoPaid;
      
      return (
        <div>
          <div className={`fw-bold ${amount > 0 ? 'text-success' : 'text-muted'}`}>
            {formatCurrency(amount, currencyCode)}
          </div>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {willBeAutoPaid && (
              <small className="badge bg-success">Auto-Paid</small>
            )}
            {hasUnpaidBalance && !willBeAutoPaid && (
              <small className="badge bg-warning text-dark">+Unpaid</small>
            )}
            {hasCredit && !willBeAutoPaid && (
              <small className="badge bg-info">-Credit</small>
            )}
            {nextMonth?.hasEscalation && (
              <small className="badge bg-secondary">Escalated</small>
            )}
          </div>
        </div>
      );
    }
    
    if (!nextMonth?.willGenerate) {
      return (
        <div>
          <div className="text-muted">No Invoice</div>
          <small className="text-muted">{nextMonth?.reason || ''}</small>
        </div>
      );
    }
    
    return <div className="text-muted">-</div>;
  };

  const rentBodyTemplate = (rowData) => {
    const currencyCode = rowData.currency?.code || 'KES';
    const baseRent = rowData.monthlyRent || 0;
    const currentRent = rowData.currentRent || 0;
    
    return (
      <div>
        <div className="fw-bold">{formatCurrency(currentRent, currencyCode)}</div>
        {rowData.hasEscalation && (
          <small className="text-info">
            Base: {formatCurrency(baseRent, currencyCode)}
          </small>
        )}
      </div>
    );
  };

  const leaseStatusBodyTemplate = (rowData) => {
    const daysToExpiry = rowData.daysUntilExpiry || 0;
    let statusClass = 'bg-success';
    let statusText = 'Active';
    
    if (daysToExpiry <= 0) {
      statusClass = 'bg-danger';
      statusText = 'Expired';
    } else if (daysToExpiry <= 30) {
      statusClass = 'bg-warning text-dark';
      statusText = `Expires in ${daysToExpiry}d`;
    }
    
    return (
      <span className={`badge ${statusClass} text-light`}>
        {statusText}
      </span>
    );
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Tenant Name',
      'Unit',
      'Monthly Rent',
      'Current Rent',
      'Has Escalation',
      'Current Invoice',
      'Amount Paid',
      'Balance',
      'Status',
      'Balance B/F',
      'Next Month Expected',
      'Currency',
      'Days to Expiry'
    ];

    const csvData = filteredData.map(item => [
      item.tenant?.fullName || '',
      item.unit?.unitName || '',
      item.monthlyRent || 0,
      item.currentRent || 0,
      item.hasEscalation ? 'Yes' : 'No',
      item.currentMonth?.totalInvoiced || 0,
      item.currentMonth?.totalPaid || 0,
      item.currentMonth?.totalBalance || 0,
      item.currentMonth?.paymentStatus || '',
      item.balanceBroughtForward || 0,
      item.nextMonth?.expectedAmount || 0,
      item.currency?.code || 'KES',
      item.daysUntilExpiry || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const year = selectedMonth.getFullYear();
    const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
    link.download = `lease_agreement_report_${year}-${month}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toastify('Report exported successfully', 'success');
  };

  if (!selectedFacilityId) {
    return (
      <div className="card">
        <div className="card-body text-center">
          Please select a facility to view the report.
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <Toast ref={toast} />
      
      <div className="card-body">
        {/* Statistics Cards */}
        {statistics && (
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card bg-primary text-white">
                <div className="card-body">
                  <h6 className="card-title">Total Leases</h6>
                  <h3>{statistics.totalLeases}</h3>
                  <small>Expiring Soon: {statistics.leasesExpiringSoon || 0}</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success text-white">
                <div className="card-body">
                  <h6 className="card-title">Current Month Invoiced</h6>
                  <h3>{formatCurrency(statistics.currentMonth?.totalInvoiced)}</h3>
                  <small>Paid: {formatCurrency(statistics.currentMonth?.totalPaid)}</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-danger text-white">
                <div className="card-body">
                  <h6 className="card-title">Outstanding Balance</h6>
                  <h3>{formatCurrency(statistics.currentMonth?.totalBalance)}</h3>
                  <small>Unpaid: {statistics.currentMonth?.unpaid}</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-info text-white">
                <div className="card-body">
                  <h6 className="card-title">Next Month Expected</h6>
                  <h3>{formatCurrency(statistics.nextMonth?.estimatedTotal)}</h3>
                  <small>{statistics.nextMonth?.expectedToGenerate} leases</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approval Status Banner */}
        {currentApproval && (
          <div className={`alert ${
            currentApproval.approvalStatus === 'Pending' ? 'alert-warning' :
            currentApproval.approvalStatus === 'Approved' ? 'alert-success' :
            'alert-danger'
          } mb-4`}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Invoice Generation Status:</strong> {currentApproval.approvalStatus}
                <br />
                <small>Period: {currentApproval.yearMonth}</small>
                {currentApproval.approvalStatus === 'Approved' && currentApproval.approvedBy && (
                  <>
                    <br />
                    <small>Approved by: {currentApproval.approvedBy.name} on {formatDate(currentApproval.approvedBy.approvalDate)}</small>
                  </>
                )}
              </div>
              {currentApproval.approvalStatus === 'Pending' && (
                <button
                  className="btn btn-warning"
                  onClick={openApprovalDialog}
                  disabled={approving || !currentUser.userId}
                >
                  <i className="ti ti-check me-1"></i>
                  Approve & Generate Invoices
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label">Select Month</label>
            <Calendar
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.value)}
              view="month"
              dateFormat="MM/yy"
              className="w-100"
              showIcon
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Search</label>
            <input
              className="form-control"
              placeholder="Tenant, Unit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Payment Status</label>
            <Dropdown
              value={statusFilter}
              options={statusOptions}
              onChange={(e) => setStatusFilter(e.value)}
              className="w-100"
              placeholder="All Statuses"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Escalations</label>
            <Dropdown
              value={escalationFilter}
              options={escalationOptions}
              onChange={(e) => setEscalationFilter(e.value)}
              className="w-100"
              placeholder="All Leases"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end mb-3">
          <button
            className="btn btn-success me-2"
            onClick={exportToCSV}
            disabled={filteredData.length === 0}
          >
            <i className="ti ti-download me-1"></i>
            Export to CSV
          </button>
          <button
            className="btn btn-primary"
            onClick={fetchReportData}
            disabled={loading}
          >
            <i className="ti ti-refresh me-1"></i>
            Refresh
          </button>
        </div>

        {/* Data Table */}
        <div className="dt-responsive table-responsive">
          <DataTable
            value={filteredData}
            emptyMessage="No lease agreements found for the selected month."
            sortMode="multiple"
            paginator
            rows={rows}
            rowsPerPageOptions={rowsPerPageOptions}
            stripedRows
            loading={loading}
            tableStyle={{ minWidth: '70rem' }}
          >
            <Column 
              field="tenant.fullName" 
              header="Tenant" 
              body={tenantBodyTemplate}
              sortable 
              style={{ minWidth: '200px' }}
            />
            <Column 
              field="unit.unitName" 
              header="Unit" 
              body={unitBodyTemplate}
              sortable 
            />
            <Column 
              field="currentRent" 
              header="Current Rent" 
              body={rentBodyTemplate}
              sortable 
            />
            <Column 
              field="currentMonth.totalInvoiced" 
              header="Current Invoice" 
              body={currentInvoiceBodyTemplate}
              sortable 
            />
            <Column 
              field="currentMonth.paymentStatus" 
              header="Status" 
              body={statusBodyTemplate}
              sortable 
              style={{ width: '150px' }}
            />
            <Column 
              field="currentMonth.totalBalance" 
              header="Balance" 
              body={balanceBodyTemplate}
              sortable 
            />
            <Column 
              field="nextMonth.expectedAmount" 
              header="Next Month Expected" 
              body={nextMonthBodyTemplate}
              sortable 
            />
            <Column 
              field="daysUntilExpiry" 
              header="Lease Status" 
              body={leaseStatusBodyTemplate}
              sortable 
              style={{ width: '150px' }}
            />
          </DataTable>
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog
        header="Approve Lease Invoice Generation"
        visible={approvalDialogVisible}
        onHide={() => {
          setApprovalDialogVisible(false);
          setComments('');
        }}
        className="w-50"
      >
        <div className="p-3">
          <div className="alert alert-info mb-4">
            <h6><i className="ti ti-info-circle me-2"></i>What will happen?</h6>
            <p className="mb-2">
              Approving this action will automatically trigger the generation of lease invoices for <strong>{currentApproval?.yearMonth}</strong>.
            </p>
            <p className="mb-0">
              <strong>Invoices will be generated for:</strong>
            </p>
            <ul className="mb-0">
              <li>All active lease agreements in the report</li>
              <li>Total expected amount: <strong>{formatCurrency(statistics?.nextMonth?.estimatedTotal)}</strong></li>
              <li>Number of leases: <strong>{statistics?.nextMonth?.expectedToGenerate}</strong></li>
            </ul>
          </div>

          <div className="mb-4">
            <div className="row">
              <div className="col-md-6">
                <p><strong>Facility:</strong> {currentApproval?.facility?.name}</p>
                <p><strong>Invoice Type:</strong> LEASE</p>
                <p><strong>Approver:</strong> {currentUser.userName}</p>
                <p><strong>User ID:</strong> {currentUser.userId}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Period:</strong> {currentApproval?.yearMonth}</p>
                <p><strong>Status:</strong> <span className="badge bg-warning">{currentApproval?.approvalStatus}</span></p>
                <p><strong>Device:</strong> {deviceInfo.deviceType}</p>
                <p><strong>IP Address:</strong> {deviceInfo.ipAddress}</p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label">Comments (Optional)</label>
            <InputTextarea
              className="form-control"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              placeholder="Add any comments about this approval..."
              autoResize
            />
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() => {
                setApprovalDialogVisible(false);
                setComments('');
              }}
              disabled={approving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleApproveInvoices}
              disabled={approving}
            >
              {approving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Approving...
                </>
              ) : (
                <>
                  <i className="ti ti-check me-1"></i>
                  Approve & Generate Invoices
                </>
              )}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LeaseAgreementReportTab;